import { CalibrationPoint, Coordinate, RealWorldCoordinate } from "./types";

/**
 * Improper similarity transform (with Y-reflection) between image pixel space and
 * real-world HK1980 grid coordinates. Ported verbatim from
 * front/src/features/define-point-revised/utils.ts.
 *
 *   x_img = a*e + b*n + tx
 *   y_img = b*e - a*n + ty
 */
export class CoordinateTransformer {
  private a: number;
  private b: number;
  private tx: number;
  private ty: number;

  constructor(calibrationPoints: CalibrationPoint[]) {
    if (calibrationPoints.length < 2) {
      throw new Error("At least 2 calibration points are required");
    }
    const [p1, p2] = calibrationPoints;

    const de = p2.realWorldCoordinate.easting - p1.realWorldCoordinate.easting;
    const dn =
      p2.realWorldCoordinate.northing - p1.realWorldCoordinate.northing;
    const dix = p2.imageCoordinate.x - p1.imageCoordinate.x;
    const diy = p2.imageCoordinate.y - p1.imageCoordinate.y;

    const D = de * de + dn * dn;
    if (D === 0) {
      throw new Error(
        "Calibration points must have different real-world coordinates",
      );
    }

    this.a = (dix * de - diy * dn) / D;
    this.b = (dix * dn + diy * de) / D;
    this.tx =
      p1.imageCoordinate.x -
      this.a * p1.realWorldCoordinate.easting -
      this.b * p1.realWorldCoordinate.northing;
    this.ty =
      p1.imageCoordinate.y -
      this.b * p1.realWorldCoordinate.easting +
      this.a * p1.realWorldCoordinate.northing;
  }

  /** Exposes the raw similarity-transform parameters (for overlay rendering). */
  getParams(): { a: number; b: number; tx: number; ty: number } {
    return { a: this.a, b: this.b, tx: this.tx, ty: this.ty };
  }

  imageToRealWorld(imageCoord: Coordinate): RealWorldCoordinate {
    const det = this.a * this.a + this.b * this.b;
    if (det === 0) {
      throw new Error(
        "Degenerate transform — calibration points coincide in image space",
      );
    }
    const dx = imageCoord.x - this.tx;
    const dy = imageCoord.y - this.ty;
    return {
      easting: (this.a * dx + this.b * dy) / det,
      northing: (this.b * dx - this.a * dy) / det,
    };
  }

  realWorldToImage(realWorldCoord: RealWorldCoordinate): Coordinate {
    return {
      x:
        this.a * realWorldCoord.easting +
        this.b * realWorldCoord.northing +
        this.tx,
      y:
        this.b * realWorldCoord.easting -
        this.a * realWorldCoord.northing +
        this.ty,
    };
  }
}
